import {lazy, Suspense, useEffect, useRef, useState, type RefObject} from "react";
import {Loader} from "@mantine/core";
// Type-only: erased at build, so the Syncfusion package stays in the lazy chunk.
import type {AjaxRequestFailureEventArgs} from "@syncfusion/ej2-react-pdfviewer";
import {buildFileDownloadHref, type PdfPosition} from "../contentUtils";
import {ResourceError} from "../ResourceError";
import type {PdfViewerHandle} from "./PdfViewer";
import classes from "./PdfDocumentBody.module.css";

// The Syncfusion viewer (plus its wasm engine) is the app's heaviest client
// dependency — lazy so it only loads when a PDF is actually opened.
const PdfViewer = lazy(() => import("./PdfViewer"));

/**
 * The PDF pane body shared by the checklist document pane and the standalone
 * PDF page — legacy PDFPreview parity minus its Download button: the viewer
 * filling the host's remaining space, a loader until the document is in and
 * the file endpoint's error mapped to ResourceError. View-only: no download
 * affordance. The host owns the header and mounts this with `key={topicId}`,
 * so switching PDFs is a fresh viewer and fresh state.
 *
 * Open to every account, demo included (legacy gates nothing here).
 *
 * `position` (a search hit's page and y) is jumped to once the document has
 * loaded, and again whenever it changes.
 */
export const PdfDocumentBody = ({
    topicId,
    viewerRef,
    onDocumentLoad,
    enableBookmark,
    position,
}: {
    topicId: string;
    /** Optional outer handle (the standalone page drives TOC navigation through it). */
    viewerRef?: RefObject<PdfViewerHandle | null>;
    onDocumentLoad?: (info: {pageCount: number}) => void;
    /** Passed to PdfViewer: needed for `goToBookmark` to jump to a position. */
    enableBookmark?: boolean;
    /** Where to open the document; needs `enableBookmark` to land below the page top. */
    position?: PdfPosition | null;
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] =
        useState<AjaxRequestFailureEventArgs | null>(null);
    const ownViewerRef = useRef<PdfViewerHandle>(null);
    const ref = viewerRef ?? ownViewerRef;
    const pageIndex = position?.pageIndex;
    const y = position?.y ?? null;

    useEffect(() => {
        if (!isLoaded || pageIndex === undefined) return;
        ref.current?.goToBookmark(pageIndex, y);
    }, [isLoaded, pageIndex, y, ref]);

    // Syncfusion fetches this by XHR; same-origin, so the BFF session rides along.
    const documentUrl = new URL(
        buildFileDownloadHref(topicId),
        window.location.origin
    ).toString();

    const renderBody = () => {
        if (loadError) {
            // ResourceError reads the RTK `{status}` shape; map Syncfusion's
            // arg so 403/404 get their proper copy.
            return <ResourceError error={{status: loadError.errorStatusCode}} />;
        }

        return (
            <>
                <Suspense fallback={null}>
                    <PdfViewer
                        ref={ref}
                        documentUrl={documentUrl}
                        onDocumentLoad={(info) => {
                            setIsLoaded(true);
                            onDocumentLoad?.(info);
                        }}
                        onAjaxRequestFailed={setLoadError}
                        enableBookmark={enableBookmark}
                    />
                </Suspense>
                {/* Covers both the lazy chunk and the document load: gone
                    once documentLoad fires. */}
                {!isLoaded && (
                    <Loader color="gneis.5" size="md" className={classes.loader} />
                )}
            </>
        );
    };

    return <div className={classes.body}>{renderBody()}</div>;
};
